import { usePricingContext } from '@vp/react-pricing-context';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    BoundedContent,
    Box,
    Button,
    CarouselSlide,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    H1,
    Link,
    ListPrice,
    Pricing,
    Row,
    ThumbnailsHero,
    ThumbnailsHeroImagesSlidesContainer,
    ThumbnailsHeroThumbnailsSlidesContainer,
    Typography,
    tokens,
} from '@vp/swan';
import { Currency, PriceFromFloat } from '@99designs/design-services-common';
import { __, getCareBriefCreateURL } from '@99designs/i18n';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { ServiceDetailsPageDataQuery } from '../../graphql';
import { buildSrcset } from '../../lib';
import { ReviewMethod } from '../ReviewMethod';
import { PrimaryCTAUrlBuilder } from './PrimaryCTAUrlBuilder';
import { addDesignerGroupOptionIfNeeded, getOverflowServiceConfig } from './overflowServiceHelper';

type Images = ServiceDetailsPageDataQuery['serviceDetailPage']['heroImages'];

export type HeaderProps = {
    heroImages: Images;
    name: string;
    price: ServiceDetailsPageDataQuery['expertServices'][0]['price'];
    sectionTitle: string;
    description: string;
    cta: ServiceDetailsPageDataQuery['serviceDetailPage']['primaryCta'];
    routeSpecifiedMpvId: string;
    printMpvId?: string;
    workId: string;
    isDesignLiveEnabled: boolean;
    environment: string;
    renderInclusions?: () => JSX.Element;
    locale: string;
    expertService?: {
        isLegacy: boolean;
    };
};

function clickEventData(ctaUrl: string) {
    const pageZone = 'Merchandising Section';
    const label = `Content:${pageZone}`;
    const ctaValue = `Get Started-CTA`;
    const source = window.location.pathname;
    const eventDetail = `${source};${ctaUrl};${pageZone};${ctaValue}`;

    return {
        label,
        eventDetail,
        navigationDetail: ctaValue,
    };
}

export function Header(props: HeaderProps) {
    const [reviewMethod, setReviewMethod] = useState('Standard');

    const getCtaUrl = useCallback(() => {
        const builder = new PrimaryCTAUrlBuilder(getCareBriefCreateURL())
            .withPrintMpvId(props.printMpvId)
            .withWorkId(props.workId)
            .withRequestType(reviewMethod);

        // Step 1: Get basic overflow config (just the service mapping)
        const baseOverflowConfig = getOverflowServiceConfig(
            props.routeSpecifiedMpvId,
            props.locale,
            reviewMethod
        );

        // Step 2: Add designer group option if needed (esMpvId !== caredesign)
        const overflowConfig = addDesignerGroupOptionIfNeeded(baseOverflowConfig);

        if (overflowConfig) {
            // Use overflow service MPV ID and designer group option
            builder.withEsMpvId(overflowConfig.esMpvId);

            if (overflowConfig.designerGroupOption) {
                builder.withServiceSelectedOptions(
                    JSON.stringify([overflowConfig.designerGroupOption])
                );
            }
        }

        return props.cta.targetUrl || builder.url;
    }, [
        props.printMpvId,
        props.workId,
        props.routeSpecifiedMpvId,
        props.cta.targetUrl,
        props.locale,
        reviewMethod,
    ]);

    const [url, setUrl] = useState('');
    const accessibleTextForDots = [''];
    const inclusions = props.renderInclusions || (() => null);
    const { pageData } = usePage();
    const pricingContext = usePricingContext();

    // Default to the highest price (i.e. taxed) while pricingContext loads
    let price = props.price.listPrice.taxed;
    if (pricingContext && !pricingContext.vatInclusive) {
        price = props.price.listPrice.untaxed;
    }

    const didRun = useRef(false);
    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;

        if (props.environment === 'development') {
            try {
                const parsedUrl = new URL(url);
                setUrl(parsedUrl.pathname + parsedUrl.search + parsedUrl.hash);
            } catch {
                // Fallback if url is already relative or malformed
                setUrl(url.replace(/^https?:\/\/[^/]+/, ''));
            }
        }
    }, [props.environment, url]);

    // Update url when reviewMethod, overflowFlag, or locale changes
    useEffect(() => {
        setUrl(getCtaUrl());
    }, [props.cta.targetUrl, reviewMethod, props.locale, getCtaUrl]);

    return (
        <BoundedContent>
            <GridContainer py={3}>
                <Row sticky>
                    <Column span={7} pl={0}>
                        <ThumbnailsHero accessibleTextForDots={accessibleTextForDots}>
                            <ThumbnailsHeroImagesSlidesContainer>
                                {props.heroImages?.map((image, i) => (
                                    <CarouselSlide
                                        key={`carouselimage1-${i}`}
                                        backgroundColor={'standard'}
                                    >
                                        <FluidImage
                                            srcSet={buildSrcset(image.cloudinaryImage)}
                                            alt={image.altTextOverride || ''}
                                            role="presentation"
                                            style={{
                                                height: 'auto',
                                                width: '100%',
                                                borderRadius: tokens.SwanBaseBorderRadius400,
                                            }}
                                        />
                                    </CarouselSlide>
                                ))}
                            </ThumbnailsHeroImagesSlidesContainer>
                            {props.heroImages?.length >= 2 && (
                                <ThumbnailsHeroThumbnailsSlidesContainer>
                                    {props.heroImages?.map((image, i) => (
                                        <CarouselSlide key={`carouselimage2-${i}`}>
                                            <FluidImage
                                                src={
                                                    image.cloudinaryImage.thumbnailCloudinaryUrl ||
                                                    ''
                                                }
                                                alt=""
                                                role="presentation"
                                            />
                                        </CarouselSlide>
                                    ))}
                                </ThumbnailsHeroThumbnailsSlidesContainer>
                            )}
                        </ThumbnailsHero>
                    </Column>
                    <Column span={5} sticky>
                        <FlexBox
                            flexDirection="column"
                            gap={'between-subsections'}
                            mt={7}
                            ml={{ xs: 0, sm: 4 }}
                        >
                            <Box>
                                <Typography>{props.sectionTitle}</Typography>
                                <Box mt={3}>
                                    <H1 mb={0} fontSkin="title-headline">
                                        {props.name}
                                    </H1>
                                </Box>
                                <Box mt={4}>
                                    <Pricing>
                                        <ListPrice fontSkin="title-subsection">
                                            <PriceFromFloat
                                                amount={price}
                                                currency={props.price.currency as Currency}
                                            />
                                        </ListPrice>
                                    </Pricing>
                                </Box>
                            </Box>
                            <Box>
                                <Typography>{props.description}</Typography>
                            </Box>
                            {inclusions()}
                            {props.isDesignLiveEnabled && (
                                <ReviewMethod
                                    reviewMethod={reviewMethod}
                                    setReviewMethod={setReviewMethod}
                                />
                            )}
                            {price === 0.0 ? (
                                <Button disabled skin="primary" width="full-width">
                                    {__('Not available')}
                                </Button>
                            ) : (
                                <Button
                                    skin="primary"
                                    width="full-width"
                                    render={(p) => (
                                        <Link
                                            to={url}
                                            className={p.className}
                                            data-testid="Primary-CTA"
                                            onClick={() => {
                                                sendNavigationEvent({
                                                    ...clickEventData(url),
                                                    ...pageData,
                                                });
                                            }}
                                        >
                                            {p.children}
                                        </Link>
                                    )}
                                >
                                    {props.cta.text}
                                </Button>
                            )}
                        </FlexBox>
                    </Column>
                </Row>
            </GridContainer>
        </BoundedContent>
    );
}
